import React, { useEffect, useState } from "react";
import { Box, Button, Container, Grid, IconButton, Typography, Avatar, Divider, Stack } from "@mui/material";
import { useParams } from "react-router-dom";
import { GetByUUID } from "../../../controllers/company/AssociatesController";
import { domain } from "../../../apiRoutes/apiBase"; 
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload, faEnvelope, faPhone, faGlobe } from "@fortawesome/free-solid-svg-icons";
import { faFacebook, faInstagram, faLinkedin, faTwitter, faWhatsapp, faYoutube, faXTwitter } from "@fortawesome/free-brands-svg-icons";
import VCard from 'vcard-creator';

// Mapeo de iconos (Se mantiene igual)
const iconMapping = {
    'Instagram': faInstagram,
    'WhatsApp': faWhatsapp,
    'X': faXTwitter,
    'Twitter': faTwitter,
    'Facebook': faFacebook,
    'Linkedin': faLinkedin,
    'YouTube': faYoutube,
    'Website': faGlobe
};

export default function BusinessCardV2() {
    const [associate, setAssociate] = useState({});
    const [socials, setSocials] = useState([]);
    
    const { uuid } = useParams();

    useEffect(() => {
        handleGetAssociate();
    }, []);

    const handleGetAssociate = async () => {
        let response = await GetByUUID(uuid);
        if (response.success === true) {
            setAssociate(response.data.associate);
            // La API ahora devuelve 'socials' en lugar de 'redes'
            setSocials(response.data.socials); 
        }
    }

    const createVCard = () => {
        const vCard = new VCard();
        // --- ADAPTACIÓN A NUEVAS VARIABLES (Inglés) ---
        vCard.addName(associate.last_name, associate.first_name)
            .addCompany(associate.company_name)
            .addJobtitle(associate.job_title)
            .addEmail(associate.email)
            .addPhoneNumber(associate.phone)
            .addAddress(associate.address)
            .addURL(`https://qud.life/businesscard/${associate.uuid}`);
            
        // Si hay imagen de perfil (URL completa), intentar añadirla (depende del soporte del navegador/vcard)
        // if(associate.profile_image) { ... }

        const blob = new Blob([vCard.toString()], { type: "text/x-vcard" });
        const url = window.URL.createObjectURL(blob);
        
        const link = document.createElement("a");
        link.href = url;
        link.download = `${associate.first_name || 'contacto'}.vcf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <Box sx={{ 
            minHeight: '100vh', 
            bgcolor: '#121212', 
            color: '#fff',
            fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
            paddingBottom: 4,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between'
        }}>
            
            {/* --- CONTENIDO PRINCIPAL --- */}
            <Box>
                {/* 1. Header / Banner (TU DISEÑO ORIGINAL) */}
                <Box
                    sx={{
                        height: { xs: '180px', md: '220px' },
                        // Mantenemos tu lógica de gradiente. Si el backend manda cover_image, se podría usar, 
                        // pero priorizamos tu estilo original de gradiente como base.
                        /*background: associate.cover_image 
                            ? `url(${associate.cover_image}) center/cover no-repeat` 
                            : `linear-gradient(135deg, ${'#173666'} 0%, #000000 100%)`,*/
                        background: `linear-gradient(135deg, ${'#173666'} 0%, #000000 100%)`,
                        position: 'relative',
                        borderBottomLeftRadius: '30px',
                        borderBottomRightRadius: '30px',
                    }} 
                >
                    {/* Overlay opcional si hay imagen de fondo para que el texto sea legible si decidieras poner algo allí */}
                    {associate.cover_image && (
                        <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, bgcolor: 'rgba(0,0,0,0.4)', borderRadius: '0 0 30px 30px' }} />
                    )}
                </Box>

                <Container maxWidth="sm" sx={{ marginTop: '-150px', paddingBottom: '20px', position: 'relative' }}>
                    
                    {/* 2. Tarjeta Principal */}
                    <Box sx={{
                        background: 'rgba(255, 255, 255, 0.05)',
                        backdropFilter: 'blur(10px)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        borderRadius: '24px',
                        padding: '30px 20px',
                        textAlign: 'center',
                        boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.37)',
                    }}>
                        
                        {/* Avatar */}
                        <Box sx={{ display: 'flex', justifyContent: 'center', marginBottom: 2 }}>
                            <Avatar
                                // CORRECCIÓN: Usamos la URL directa del backend (profile_image)
                                src={associate.profile_image} 
                                alt={associate.first_name}
                                sx={{
                                    width: 140,
                                    height: 140,
                                    border: '4px solid #121212',
                                    boxShadow: '0px 4px 20px rgba(0,0,0,0.5)',
                                }}
                            />
                        </Box>

                        {/* Textos Informativos (Variables en Inglés) */}
                        <Typography variant="h4" fontWeight="700" sx={{ letterSpacing: '0.5px' }}>
                            {associate.first_name} {associate.last_name}
                        </Typography>
                        
                        <Typography variant="subtitle1" sx={{ color: '#aaa', fontWeight: 500, mb: 1, textTransform: 'uppercase', fontSize: '0.85rem', letterSpacing: '1px' }}>
                            {associate.job_title}
                        </Typography>
                        
                        <Typography variant="h6" sx={{ color: '#fff', fontWeight: 'bold', mb: 2 }}>
                            {associate.merchant_name}
                        </Typography>

                        {/* Descripción (Usando company_description o description según venga) */}
                        {associate.merchant_description && (
                            <Typography variant="body2" sx={{ color: '#cccccc', mb: 3, lineHeight: 1.6, maxWidth: '90%', marginX: 'auto' }}>
                                {associate.merchant_description}
                            </Typography>
                        )}

                        {/* Redes Sociales */}
                        <Stack direction="row" spacing={2} justifyContent="center" sx={{ mb: 4 }}>
                            {socials.map((item, index) => (
                                <IconButton
                                    key={index}
                                    href={item.url} // Antes 'enlace'
                                    target="_blank"
                                    sx={{
                                        color: '#fff',
                                        bgcolor: 'rgba(255,255,255,0.1)',
                                        '&:hover': { bgcolor: 'primary.main', transform: 'translateY(-3px)' },
                                        transition: 'all 0.3s ease',
                                        width: 45,
                                        height: 45
                                    }}
                                >
                                    <FontAwesomeIcon icon={iconMapping[item.name] || faGlobe} size="sm" />
                                </IconButton>
                            ))}
                        </Stack>

                        {/* Botones de Acción */}
                        <Grid container spacing={2}>
                            {/* Guardar Contacto */}
                            <Grid item xs={12}>
                                <Button
                                    fullWidth
                                    variant="contained"
                                    onClick={createVCard}
                                    size="large"
                                    sx={{
                                        borderRadius: '50px',
                                        padding: '12px',
                                        fontWeight: 'bold',
                                        background: 'linear-gradient(90deg, #00C6FF 0%, #0072FF 100%)',
                                        boxShadow: '0 4px 15px rgba(0, 114, 255, 0.4)',
                                        textTransform: 'none',
                                        fontSize: '1rem'
                                    }}
                                >
                                    <FontAwesomeIcon icon={faDownload} style={{ marginRight: 8 }} />
                                    Guardar Contacto
                                </Button>
                            </Grid>

                            {/* Email */}
                            <Grid item xs={6}>
                                <Button
                                    fullWidth
                                    variant="outlined"
                                    onClick={() => window.location.href = `mailto:${associate.email}`}
                                    sx={{
                                        borderRadius: '15px',
                                        borderColor: 'rgba(255,255,255,0.2)',
                                        color: '#fff',
                                        padding: '10px',
                                        textTransform: 'none',
                                        '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.05)' }
                                    }}
                                >
                                    <FontAwesomeIcon icon={faEnvelope} style={{ marginRight: 8 }} />
                                    Email
                                </Button>
                            </Grid>
                            
                            {/* Llamar */}
                            <Grid item xs={6}>
                                <Button
                                    fullWidth
                                    variant="outlined"
                                    onClick={() => window.location.href = `tel:${associate.phone}`} // Antes telefono
                                    sx={{
                                        borderRadius: '15px',
                                        borderColor: 'rgba(255,255,255,0.2)',
                                        color: '#fff',
                                        padding: '10px',
                                        textTransform: 'none',
                                        '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.05)' }
                                    }}
                                >
                                    <FontAwesomeIcon icon={faPhone} style={{ marginRight: 8 }} />
                                    Llamar
                                </Button>
                            </Grid>

                             {/* Visitar Perfil */}
                             <Grid item xs={12}>
                                <Button
                                    fullWidth
                                    variant="text"
                                    target="_blank"
                                    href={`${domain}merchants/${associate.merchant_uuid}/profile`}
                                    sx={{ color: '#aaa', textTransform: 'none', fontSize: '0.85rem', mt: 1 }}
                                >
                                    Ver perfil de empresa <FontAwesomeIcon icon={faGlobe} style={{ marginLeft: 5 }} />
                                </Button>
                            </Grid>
                        </Grid>
                    </Box>
                </Container>
            </Box>

            {/* --- FOOTER DE COLABORACIÓN --- */}
            <Box sx={{ 
                marginTop: 4, 
                padding: '20px 0', 
                textAlign: 'center',
                borderTop: '1px solid rgba(255,255,255,0.05)',
                //bgcolor: '#0a0a0a'
                bgcolor:'#121212'
            }}>
                <Typography variant="caption" sx={{ color: '#666', textTransform: 'uppercase', letterSpacing: 1.5, display: 'block', mb: 1 }}>
                    {
                        associate.ally_name !== null
                        ?'En colaboración con'
                        :'Powered by'
                    }
                </Typography>
                
                <Stack 
                    direction="row" 
                    spacing={3} 
                    justifyContent="center" 
                    alignItems="center"
                    divider={
                        associate.ally_name !== null
                        ?<Divider orientation="vertical" flexItem sx={{ bgcolor: '#333' }} />
                        :null
                    }
                >
                    {/* QUD */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ color: '#fff', fontWeight: 700, letterSpacing: 1 }}>
                            QUD
                        </Typography>
                    </Box>

                    {/* ALIADO (Dinámico) */}
                    {
                        associate.ally_name !== null
                        ?
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" sx={{ color: '#fff', fontWeight: 700, letterSpacing: 1 }}>
                                {associate.ally_name}
                            </Typography>
                        </Box>
                        :null
                    }
                    
                </Stack>
            </Box>

        </Box>
    );
}